import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authClient } from '@/lib/auth-client'
import { Route as DashboardRoute } from '@/routes/_app/_auth/dashboard/_layout.index'
import * as validators from '@/shared/utils/validators'

export function OnboardingUsernamePage() {
  const { t } = useTranslation()
  const { data: user } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { mutateAsync: completeOnboarding } = useMutation({
    mutationFn: useConvexMutation(
      api.features.onboarding.onboarding.completeOnboarding,
    ),
  })
  const navigate = useNavigate()

  const form = useForm({
    defaultValues: {
      username: '',
      organizationName: '',
      firstName: '',
      lastName: '',
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)

      const { data: org } = await authClient.organization.create({
        name: value.organizationName,
        slug: value.organizationName.toLowerCase().replace(/\s+/g, '-'),
      })

      if (!org) {
        setIsSubmitting(false)
        return
      }

      await completeOnboarding({
        username: value.username,
        firstName: value.firstName || undefined,
        lastName: value.lastName || undefined,
      })

      setIsSubmitting(false)
    },
  })

  useEffect(() => {
    if (user?.username) {
      navigate({ to: DashboardRoute.fullPath })
    }
  }, [user?.username])

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 select-none text-6xl">👋</span>
        <h3 className="text-center text-2xl font-medium text-primary">
          {t('onboarding.username.title')}
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          {t('onboarding.username.subtitle')}
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="organizationName" className="sr-only">
            {t('onboarding.username.organizationNameLabel')}
          </label>
          <form.Field
            name="organizationName"
            validators={{
              onSubmit: validators.organizationName,
            }}
            children={(field) => (
              <Input
                placeholder={t(
                  'onboarding.username.organizationNamePlaceholder',
                )}
                autoComplete="organization"
                required
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  'border-destructive focus-visible:ring-destructive'
                }`}
              />
            )}
          />
          {(form.state.fieldMeta.organizationName?.errors?.length ?? 0) > 0 && (
            <span className="text-sm text-destructive dark:text-destructive-foreground">
              {form.state.fieldMeta.organizationName?.errors.join(' ')}
            </span>
          )}
        </div>

        <div className="flex w-full gap-3">
          <div className="flex flex-1 flex-col gap-1.5">
            <label htmlFor="firstName" className="sr-only">
              {t('onboarding.username.firstNameLabel')}
            </label>
            <form.Field
              name="firstName"
              children={(field) => (
                <Input
                  placeholder={t('onboarding.username.firstNamePlaceholder')}
                  autoComplete="given-name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="bg-transparent"
                />
              )}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <label htmlFor="lastName" className="sr-only">
              {t('onboarding.username.lastNameLabel')}
            </label>
            <form.Field
              name="lastName"
              children={(field) => (
                <Input
                  placeholder={t('onboarding.username.lastNamePlaceholder')}
                  autoComplete="family-name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="bg-transparent"
                />
              )}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="username" className="sr-only">
            {t('onboarding.username.usernameLabel')}
          </label>
          <form.Field
            name="username"
            validators={{
              onSubmit: validators.username,
            }}
            children={(field) => (
              <Input
                placeholder={t('onboarding.username.usernamePlaceholder')}
                autoComplete="off"
                required
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  'border-destructive focus-visible:ring-destructive'
                }`}
              />
            )}
          />
          {(form.state.fieldMeta.username?.errors?.length ?? 0) > 0 && (
            <span className="text-sm text-destructive dark:text-destructive-foreground">
              {form.state.fieldMeta.username?.errors.join(' ')}
            </span>
          )}
        </div>

        <Button type="submit" size="sm" className="w-full">
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            t('onboarding.username.continue')
          )}
        </Button>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        {t('onboarding.username.hint')}
      </p>
    </div>
  )
}
